import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { tap, catchError, switchMap, finalize, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';

import { environment } from '../../environments/environment';
import { TokenResponseDto } from '../models/dto/token-response.dto';
import { ApiResponse } from '../models/api/api-response.model';
import { ProfileDto } from '../models/dto/profile.dto';
import { ForgotPasswordParameter } from '../models/parameter/forgot-password.parameter';
import { ResetPasswordParameter } from '../models/parameter/reset-password.parameter';
import { TokenRequestParameter } from '../models/parameter/token-request.parameter';
import { VerifyOtpParameter } from '../models/parameter/verify-otp.parameter';
import { UpdateProfileParameter } from '../models/parameter/update-profile.parameter';
import { UpdateDietProfileParameter } from '../models/parameter/update-diet-profile.parameter';
import { DietProfileDto } from '../models/dto/diet-profile.dto';
import { ChangePasswordParameter } from '../models/parameter/change-password.parameter';
import { ChangeEmailParameter } from '../models/parameter/change-email.parameter';
import { SessionDto } from '../models/dto/session.dto';
import { PersonalAccessTokenDto } from '../models/dto/personal-access-token.dto';
import { UUID } from '../shared/types';
import { LocalizationService } from '../core/services/localization.service';
import { SKIP_GLOBAL_LOADER } from '../core/http/http-context-tokens';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _http = inject(HttpClient);
  private readonly _router = inject(Router);
  private readonly _localizationService = inject(LocalizationService);

  private readonly _API_URL = environment.apiUrl + '/auth';
  private readonly _CLIENT_ID = environment.clientId;

  private readonly _APP_PREFIX = 'webapp-';
  private readonly _TOKEN_KEY = this._APP_PREFIX + 'access-token';
  private readonly _REFRESH_TOKEN_KEY = this._APP_PREFIX + 'refresh-token';

  private readonly _currentProfileSubject =
    new BehaviorSubject<ProfileDto | null>(null);
  public readonly currentProfile$ = this._currentProfileSubject.asObservable();

  private readonly _isAuthenticatedSubject = new BehaviorSubject<boolean>(
    false,
  );
  public readonly isAuthenticated$ =
    this._isAuthenticatedSubject.asObservable();

  constructor() {}

  login(
    username: string,
    password: string,
    rememberMe: boolean,
  ): Observable<ApiResponse<ProfileDto>> {
    const body: TokenRequestParameter = {
      grantType: 'password',
      username,
      password,
      clientId: this._CLIENT_ID,
    };

    return this._http
      .post<ApiResponse<TokenResponseDto>>(`${this._API_URL}/token`, body)
      .pipe(
        switchMap((response) => {
          this._setSession(response.data, rememberMe);
          return this.getProfile();
        }),
        catchError(this._handleError),
      );
  }

  private _refreshInProgress$: Observable<ApiResponse<TokenResponseDto>> | null = null;

  refreshTokenOnce(): Observable<ApiResponse<TokenResponseDto>> {
    if (!this._refreshInProgress$) {
      this._refreshInProgress$ = this.refreshToken().pipe(
        finalize(() => (this._refreshInProgress$ = null)),
        shareReplay(1),
      );
    }
    return this._refreshInProgress$;
  }

  refreshToken(): Observable<ApiResponse<TokenResponseDto>> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available.'));
    }
    const body: TokenRequestParameter = {
      grantType: 'refresh_token',
      refreshToken,
      clientId: this._CLIENT_ID,
    };

    return this._http
      .post<ApiResponse<TokenResponseDto>>(`${this._API_URL}/token`, body, {
        context: new HttpContext().set(SKIP_GLOBAL_LOADER, true),
      })
      .pipe(
        tap((response) => {
          this._setSession(response.data);
        }),
        catchError((error) => {
          this._removeSession();
          return throwError(() => error);
        }),
      );
  }

  forgotPassword(
    params: ForgotPasswordParameter,
  ): Observable<ApiResponse<void>> {
    return this._http
      .post<ApiResponse<void>>(`${this._API_URL}/forgot-password`, params)
      .pipe(catchError(this._handleError));
  }

  resetPassword(params: ResetPasswordParameter): Observable<ApiResponse<void>> {
    return this._http
      .post<ApiResponse<void>>(`${this._API_URL}/reset-password`, params)
      .pipe(catchError(this._handleError));
  }

  verifyOtp(params: VerifyOtpParameter): Observable<ApiResponse<void>> {
    return this._http
      .post<ApiResponse<void>>(`${this._API_URL}/verify-otp`, params)
      .pipe(catchError(this._handleError));
  }

  resendOtp(email: string): Observable<ApiResponse<void>> {
    return this._http
      .post<ApiResponse<void>>(`${this._API_URL}/resend-otp`, { email })
      .pipe(catchError(this._handleError));
  }

  getProfile(): Observable<ApiResponse<ProfileDto>> {
    return this._http
      .get<ApiResponse<ProfileDto>>(`${this._API_URL}/profile`)
      .pipe(
        tap((response) => {
          this._currentProfileSubject.next(response.data);
          this._localizationService.setLanguage(response.data.preferredLanguage);
        }),
        catchError(this._handleError),
      );
  }

  updateProfile(
    params: UpdateProfileParameter,
  ): Observable<ApiResponse<ProfileDto>> {
    return this._http
      .put<ApiResponse<ProfileDto>>(`${this._API_URL}/profile`, params)
      .pipe(
        tap((response) => {
          this._currentProfileSubject.next(response.data);
          this._localizationService.setLanguage(response.data.preferredLanguage);
        }),
        catchError(this._handleError),
      );
  }

  getDietProfile(): Observable<ApiResponse<DietProfileDto>> {
    return this._http
      .get<ApiResponse<DietProfileDto>>(`${this._API_URL}/profile/diet`)
      .pipe(catchError(this._handleError));
  }

  updateDietProfile(
    params: UpdateDietProfileParameter,
  ): Observable<ApiResponse<DietProfileDto>> {
    return this._http
      .put<ApiResponse<DietProfileDto>>(`${this._API_URL}/profile/diet`, params)
      .pipe(catchError(this._handleError));
  }

  changePassword(
    params: ChangePasswordParameter,
  ): Observable<ApiResponse<void>> {
    return this._http
      .post<
        ApiResponse<void>
      >(`${this._API_URL}/profile/change-password`, params)
      .pipe(catchError(this._handleError));
  }

  listSessions(): Observable<ApiResponse<SessionDto[]>> {
    return this._http
      .get<ApiResponse<SessionDto[]>>(`${this._API_URL}/sessions`)
      .pipe(catchError(this._handleError));
  }

  revokeSession(id: UUID): Observable<ApiResponse<void>> {
    return this._http
      .delete<ApiResponse<void>>(`${this._API_URL}/sessions/${id}`)
      .pipe(catchError(this._handleError));
  }

  createPersonalAccessToken(): Observable<ApiResponse<PersonalAccessTokenDto>> {
    return this._http
      .post<ApiResponse<PersonalAccessTokenDto>>(`${this._API_URL}/personal-tokens`, {})
      .pipe(catchError(this._handleError));
  }

  revokeAllSessions(): Observable<ApiResponse<void>> {
    return this._http
      .delete<ApiResponse<void>>(`${this._API_URL}/sessions`)
      .pipe(catchError(this._handleError));
  }

  requestEmailChange(params: ChangeEmailParameter): Observable<ApiResponse<void>> {
    return this._http
      .post<ApiResponse<void>>(`${this._API_URL}/profile/change-email`, params)
      .pipe(catchError(this._handleError));
  }

  confirmEmailChange(token: string): Observable<ApiResponse<void>> {
    return this._http
      .post<ApiResponse<void>>(`${this._API_URL}/confirm-email-change`, { token })
      .pipe(catchError(this._handleError));
  }

  logout(): void {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this._removeSession();
      this._router.navigate(['/login']);
      return;
    }

    this._http
      .post(`${this._API_URL}/logout`, { refreshToken })
      .pipe(
        finalize(() => {
          this._removeSession();
          this._router.navigate(['/login']);
        }),
      )
      .subscribe();
  }

  public getAccessToken(): string | null {
    return (
      sessionStorage.getItem(this._TOKEN_KEY) ||
      localStorage.getItem(this._TOKEN_KEY)
    );
  }

  public getRefreshToken(): string | null {
    return (
      sessionStorage.getItem(this._REFRESH_TOKEN_KEY) ||
      localStorage.getItem(this._REFRESH_TOKEN_KEY)
    );
  }

  public checkAuthStatus(): Observable<any> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return of(null);
    }
    return this.refreshTokenOnce().pipe(
      switchMap(() => this.getProfile()),
      catchError(() => {
        this._removeSession();
        return of(null);
      }),
    );
  }

  private _setSession(tokens: TokenResponseDto, rememberMe?: boolean): void {
    let storage: Storage;

    if (typeof rememberMe === 'boolean') {
      storage = rememberMe ? localStorage : sessionStorage;
    } else {
      storage = localStorage.getItem(this._REFRESH_TOKEN_KEY)
        ? localStorage
        : sessionStorage;
    }

    this._removeSession();

    storage.setItem(this._TOKEN_KEY, tokens.accessToken);
    storage.setItem(this._REFRESH_TOKEN_KEY, tokens.refreshToken);

    this._isAuthenticatedSubject.next(true);
  }

  private _removeSession(): void {
    localStorage.removeItem(this._TOKEN_KEY);
    localStorage.removeItem(this._REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(this._TOKEN_KEY);
    sessionStorage.removeItem(this._REFRESH_TOKEN_KEY);

    this._isAuthenticatedSubject.next(false);
    this._currentProfileSubject.next(null);
  }

  private _handleError = (error: HttpErrorResponse): Observable<never> => {
    return throwError(() => error);
  };
}
